import { Component, EventEmitter, Input, Output } from "@angular/core";
import { NavController, NavParams, ViewController } from "ionic-angular";
import { Order, OrderComponent } from "models/order-management/models";
import { isProfessionalProvider } from "models/resource-management/company-resource-type-guards";
import { OrderFormState } from "pages/orders-entry-om/add-order/order-form-state";
import { UtilityService } from "providers/utility-service/utility-service";
import { StateManagementService } from "../../../providers/state-management/state-management.service";
import {
  OrderComponentInterface,
  OrderComponentPropertyUpdate,
  OrderCreateRequestUpdate,
} from "../order-components-interface";

const E_SIGN_VERIFICATION = "eSignVerification";
const DECLINED = "declined";
const READ_BACK_AND_VERIFIED = "readBackAndVerified";
const PRN = "PRN";
const MAR = "MAR";
const IS_COMPOUND = "isCompound";

@Component({
  selector: "checkbox",
  templateUrl: "checkbox.html",
})
export class VerificationComponent implements OrderComponentInterface {
  /****************************************************************************
   * Inputs                                                                   *
   ****************************************************************************/
  @Input() orderComponent: OrderComponent;
  @Input() orderCreateRequest: Order;
  @Input() set orderFormState(orderFormState: OrderFormState) {
    const componentField = this.orderComponent.properties[0].name;
    const shouldClear = OrderFormState.shouldClear(
      componentField,
      this._orderFormState,
      orderFormState
    );
    this._orderFormState = orderFormState;

    this.isProfesionalProvider = isProfessionalProvider(
      this._orderFormState.credentialedResource
    );

    this.shouldShow = this.getShouldShow();

    // Clear if should clear or should not show and currently checked
    if (shouldClear || (!this.shouldShow && this.checked)) {
      this.orderCreateRequestEvent.emit({
        deletes: [componentField as keyof Order],
      });
      this.shouldShow = false;
      this.checked = false;
      if ([DECLINED, PRN].includes(componentField)) {
        this.orderFormStateEvent.emit({
          [componentField]: false,
        });
      }
    }
    this.checkIsValid();
  }

  /****************************************************************************
   * Outputs                                                                  *
   ****************************************************************************/
  @Output() amendedOrderRequestEvent =
    new EventEmitter<OrderComponentPropertyUpdate>();
  @Output() orderCreateRequestEvent =
    new EventEmitter<OrderCreateRequestUpdate>();
  @Output() orderFormStateEvent = new EventEmitter<OrderFormState>();
  @Output() isValidEvent = new EventEmitter<boolean>();

  /****************************************************************************
   * State                                                                    *
   ****************************************************************************/
  checked: boolean = false;
  shouldShow: boolean;
  label: string;
  boldLabel: boolean;
  isProfesionalProvider: boolean;
  _orderFormState: OrderFormState;

  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    public viewCtrl: ViewController,
    public stateData: StateManagementService,
    public utilityService: UtilityService
  ) {}

  /****************************************************************************
   * Lifecycle Methods                                                        *
   ****************************************************************************/
  ngOnInit() {
    this.shouldShow = this.getShouldShow();

    this.label = this.isProfesionalProvider
      ? this.orderComponent.componentLabel.replace(
          "[ProviderName]",
          this._orderFormState.credentialedResource.findDisplayName()
        )
      : this.orderComponent.componentLabel;
    this.boldLabel = [MAR, E_SIGN_VERIFICATION].includes(
      this.orderComponent.properties[0].name
    );
    // Set default on the request
    switch (this._orderFormState.formType) {
      case "amendment":
        this.setAmendedValue();
      case "convertToOrder":
        this.setOrderIsCompoundValue();
      default:
        this.onChecked(this.checked);
    }
  }

  /****************************************************************************
   * Setter Methods                                                           *
   ****************************************************************************/
  setAmendedValue() {
    const componentField: string = this.orderComponent.properties[0].name;
    this.checked = this.orderCreateRequest[componentField];
  }

  setOrderIsCompoundValue() {
    this.setAmendedValue();
    this.onChecked(this.checked);
  }

  /****************************************************************************
   * Component Related Methods                                                *
   ****************************************************************************/
  getShouldShow(): boolean {
    const componentField = this.orderComponent.properties[0].name;
    return [
      this.shouldShowDeclineCheckbox,
      this.shouldShowEsignCheckbox,
      this.shouldShowPRNCheckbox,
      this.shouldShowReadBackAndVerified,
      this.shouldShowWhileDeclined,
    ].every((fn) => fn(componentField));
  }

  shouldShowReadBackAndVerified = (componentField: string): boolean => {
    if (READ_BACK_AND_VERIFIED === componentField) {
      return this._orderFormState.approvalType == Order.ApprovalTypeEnum.Verbal;
    }
    return true;
  };

  shouldShowDeclineCheckbox = (componentField: string): boolean => {
    if (DECLINED === componentField) {
      if (this._orderFormState.discipline === "Skilled Nurse") {
        return false;
      }
    }
    return true;
  };

  shouldShowEsignCheckbox = (componentField: string): boolean => {
    if (E_SIGN_VERIFICATION === componentField) {
      return this._orderFormState.isSameProvider;
    }
    return true;
  };

  shouldShowPRNCheckbox = (componentField: string): boolean => {
    if (
      PRN === componentField &&
      this.orderCreateRequest.orderType === "VISIT_FREQUENCY"
    ) {
      return (
        this._orderFormState.discipline &&
        this._orderFormState.discipline.toLowerCase() !== "hospice aide"
      );
    }
    return true;
  };

  shouldShowWhileDeclined = (componentField: string): boolean => {
    if (this._orderFormState.declined && [PRN].includes(componentField)) {
      return false;
    }
    return true;
  };

  onChecked(event: boolean) {
    const componentField = this.orderComponent.properties[0].name;
    // MAR isn't actually a field on the order, so we skip
    if (![MAR].includes(componentField)) {
      const updateObject: OrderCreateRequestUpdate = { updates: {} };
      updateObject.updates[componentField] = event;
      this.orderCreateRequestEvent.emit(updateObject);
    }
    if ([IS_COMPOUND, MAR, PRN, DECLINED].includes(componentField)) {
      this.orderFormStateEvent.emit({
        [componentField]: event,
      });
    }

    this.checkIsValid();
  }

  checkIsValid() {
    const componentField = this.orderComponent.properties[0].name;
    if (componentField === IS_COMPOUND && this.checked) {
      this.isValidEvent.emit(true);
      return;
    }
    if (this.isRequired() && !this.checked && this.shouldShow) {
      this.isValidEvent.emit(false);
    } else {
      this.isValidEvent.emit(true);
    }
  }

  isRequired() {
    return (
      this.orderComponent.properties[0].required ||
      this.orderComponent.properties[0].name === READ_BACK_AND_VERIFIED ||
      (this.isProfesionalProvider &&
        this.orderComponent.properties[0].name === E_SIGN_VERIFICATION)
    );
  }

  isDisabled() {
    return (
      !this.orderComponent.properties[0].amendable &&
      this._orderFormState.formType === "amendment"
    );
  }
  /****************************************************************************
   * Cypress-Related ("data-cy") Methods                                      *
   ****************************************************************************/
  toKebabCase(label: string): string {
    if (label) {
      return label
        .replace(/^\s+|\s+$/gm, "")
        .replace(/([a-z])([A-Z])/g, "$1-$2")
        .replace(/[\s_]+/g, "-")
        .toLowerCase();
    }
    return "";
  }

  createDataCyAttribute(component: string, option: string): string {
    return `${this.toKebabCase(component)}-option-${this.toKebabCase(option)}`;
  }
}



// WEBPACK FOOTER //
// ./src/components/order-management/checkbox/checkbox.ts